// Correção UX v2 §5.2.1 — card da listagem em 4 LINHAS (decisão do Breno
// sobrescreve o card de 3 linhas do wireframe; anatomia .wf-scard2 portada
// verbatim em src/styles/wf-sessions.css):
//   L1: bolinha de status (verde=aberta, vermelha=encerrada) + título + lápis
//   L2: badges runtime + canal (esq) · contagem de mensagens (dir)
//   L3: botão copiar + ID curto (esq) · NOME do companion da sessão (dir)
//   L4: "start:" data+hora · "end:" data+hora ou "—" se aberta
import { useState } from 'react';
import type { KeyboardEvent, MouseEvent } from 'react';
import { Copy, Lock, Zap, Radio } from 'lucide-react';
import type { Session } from '@/lib/api';
import { cn } from '@/lib/utils';
import { fmtDayTime } from '@/lib/format';
import { EditableTitle } from './EditableTitle';
import {
  deriveSessionRuntime,
  sessionDisplayTitle,
  shortSessionId,
  copySessionId,
} from './session-utils';

const CHANNEL_LABELS: Record<string, string> = {
  dashboard: 'Dashboard',
  telegram: 'Telegram',
  cli: 'CLI',
  voice: 'Voice',
};

interface SessionCardProps {
  session: Session;
  /** Nome do companion da sessão — resolvido na page via lista cacheada
   *  (1 fetch de companions, não N+1). null/undefined → "—". */
  companionName?: string | null;
  isSelected?: boolean;
  onClick?: () => void;
  /** Persiste o título (page chama api.renameSession + trata erro com toast). */
  onRename: (title: string) => Promise<void>;
  /** Notifica cópia do ID (page mostra toast). */
  onCopied?: (ok: boolean) => void;
}

export function SessionCard({
  session,
  companionName,
  isSelected,
  onClick,
  onRename,
  onCopied,
}: SessionCardProps) {
  const title = sessionDisplayTitle(session);
  const runtime = deriveSessionRuntime(session);
  const channelLabel = CHANNEL_LABELS[session.channel] ?? session.channel;
  const msgCount = session.message_count ?? session.messages?.length ?? 0;

  // Bolinha de status (Breno 11/06) — 3 estados. BUG anterior: dependia de
  // `session.status`, que a LISTA do backend não envia → tudo vermelho.
  //   verde    = aberta (sem ended_at) com atividade recente
  //   amarela  = aberta mas sem atividade recente (ociosa, sem "end")
  //   vermelha = encerrada (ended_at preenchido ou status 'ended')
  const isEnded = Boolean(session.ended_at) || session.status === 'ended';
  const STALE_MS = 24 * 60 * 60 * 1000;
  const lastActivity = session.updated_at ?? session.started_at;
  // Snapshot único do relógio (lazy initializer — render puro p/ o compiler;
  // staleness não precisa ser reativa ao segundo).
  const [nowMs] = useState(() => Date.now());
  const isStale =
    !isEnded && lastActivity
      ? nowMs - new Date(lastActivity).getTime() > STALE_MS
      : false;
  const dotTone = isEnded ? 'wf-dot--bad' : isStale ? 'wf-dot--wait' : 'wf-dot--ok';
  const dotTitle = isEnded ? 'Sessão encerrada' : isStale ? 'Sessão aberta (ociosa)' : 'Sessão aberta';

  // L4 — data+hora SEMPRE completas (minutos cortavam por overflow): formato
  // compacto "dd/mm hh:mm" (sem a vírgula do fmtDayTime).
  const fmtL4 = (d: string) => fmtDayTime(d).replace(',', '');

  function handleKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    // BUG DO ESPAÇO (correção v2 §5.2.2) — defesa 2 de 2: eventos de teclado
    // que borbulham de filhos (input do rename, botões internos) NÃO ativam o
    // card; só teclas disparadas no próprio card.
    if (e.target !== e.currentTarget) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick?.();
    }
  }

  async function handleCopy(e: MouseEvent) {
    e.stopPropagation();
    const ok = await copySessionId(session.id);
    onCopied?.(ok);
  }

  return (
    // div[role=button] (não <button>) — permite botões internos (lápis/copiar)
    // sem <button> aninhado inválido.
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      className={cn(
        'wf-scard2 w-full text-left',
        isSelected && 'active',
        runtime.external && 'locked',
      )}
    >
      {/* L1 — bolinha de status + título + lápis */}
      <div className="wf-s-top">
        <span className={cn('wf-dot shrink-0', dotTone)} title={dotTitle} />
        <EditableTitle value={title} onSave={onRename} className="flex-1 min-w-0" textClassName="wf-s-title" />
      </div>

      {/* L2 — runtime + canal (esq) · nº msgs (dir) */}
      <div className="wf-s-mid">
        <span className={cn('wf-rtb', runtime.external ? 'openclaw' : 'raios')}>
          {runtime.external ? <Lock aria-hidden="true" /> : <Zap aria-hidden="true" />}
          {runtime.label}
        </span>
        <span className="wf-chb">
          <Radio aria-hidden="true" />
          {channelLabel}
        </span>
        <span className="wf-s-msgs">{msgCount} msgs</span>
      </div>

      {/* L3 — copiar + ID curto (esq) · nome do companion (dir) */}
      <div className="wf-s-bot">
        <span className="wf-s-id">
          <button
            type="button"
            className="wf-copybtn"
            onClick={(e) => void handleCopy(e)}
            aria-label="Copiar ID"
            title="Copiar ID"
          >
            <Copy />
          </button>
          {shortSessionId(session.id)}
        </span>
        <span className="wf-s-compname" title={companionName ?? undefined}>
          {companionName ?? '—'}
        </span>
      </div>

      {/* L4 — start/end completos, sem truncamento (space-between + fonte
          compacta no CSS; formato dd/mm hh:mm) */}
      <div className="wf-s-l4">
        <span>start: {fmtL4(session.started_at)}</span>
        <span>end: {session.ended_at ? fmtL4(session.ended_at) : '—'}</span>
      </div>
    </div>
  );
}
